import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export type Profile = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: "parent" | "admin";
  is_coach: boolean;
};

export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, email, first_name, last_name, phone, role, is_coach")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data as Profile | null;
}

/** Where a signed-in user should land: verify email first, then admin or family home. */
export async function resolvePostAuthRedirect(): Promise<string> {
  const { data } = await supabase.auth.getUser();
  const user = data.user;
  if (!user) return "/login";
  if (!user.email_confirmed_at) return "/verify-email";
  const profile = await getProfile(user.id);
  return profile?.role === "admin" ? "/admin" : "/home";
}

/** Current session, kept in sync with Supabase auth events. `undefined` while loading. */
export function useSession(): Session | null | undefined {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);
  return session;
}

/** Sign out any existing session first so a new sign-in never mixes two accounts. */
export async function clearExistingSession() {
  const { data } = await supabase.auth.getSession();
  if (data.session) await supabase.auth.signOut();
}

export function authRedirectUrl(path = "/auth/callback") {
  return `${window.location.origin}${path}`;
}

/** Formats a US phone number as the user types: (555) 555-5555. */
export function formatUsPhone(value: string): string {
  // Drop a leading country code "1" (no US area code starts with 1).
  const digits = value.replace(/\D/g, "").replace(/^1/, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Turns Supabase auth errors into messages parents can act on. */
export function friendlyAuthError(message: string): string {
  if (/invalid login credentials/i.test(message)) return "Email or password is incorrect.";
  if (/email not confirmed/i.test(message)) return "Please confirm your email first. Check your inbox.";
  if (/already registered|already exists/i.test(message)) return "An account with this email already exists. Sign in instead.";
  if (/rate limit|too many/i.test(message)) return "Too many attempts. Please wait a minute and try again.";
  if (/password/i.test(message) && /least|short|weak/i.test(message)) return "Please choose a stronger password (at least 8 characters).";
  return "Something went wrong. Please try again.";
}
